import React from 'react';
import logo from '../assets/img/logo.png';

/* ═══════════════════════════════════════════════════════════════════════════
   COQUILLE DES PAGES D'AUTHENTIFICATION

   Connexion, activation et mot de passe oublié forment un même parcours : ils
   doivent se ressembler. Une coquille partagée évite que trois pages écrites
   à des moments différents divergent en marges, en tailles et en tons.

   Palette fixe et non thémée, volontairement. Ces pages s'affichent avant
   toute session : les variables de thème sont posées par le gabarit du
   back-office, qui n'est pas monté ici. S'y référer donnerait du texte
   invisible sur fond invisible.
   ═══════════════════════════════════════════════════════════════════════════ */

export const ENCRE = '#0A1128';
export const ACCENT = '#5EBD4F';
export const BORDURE = '#E3E9E4';
export const FOND = '#F7F9F8';

export default function CarteAuth({ surtitre, titre, description, children, pied }) {
  return (
    <div className="min-h-screen flex items-center justify-center p-5 sm:p-6 font-sans"
         style={{ background: FOND }}>
      <div className="w-full max-w-[430px]">
        <div className="text-center mb-7">
          <img
            src={logo}
            alt="ASSO-PRO"
            className="h-12 mx-auto mb-6 object-contain"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
          {surtitre && (
            <p className="text-[11px] font-medium uppercase tracking-[0.16em] mb-2"
               style={{ color: ACCENT }}>
              {surtitre}
            </p>
          )}
          <h1 className="titre-page" style={{ color: ENCRE }}>
            {titre}
          </h1>
          {description && (
            <p className="text-[14px] leading-relaxed mt-3 text-[var(--text-muted)] max-w-[360px] mx-auto">
              {description}
            </p>
          )}
        </div>

        {/* Rayon de panneau, pas de bouton : le passage systematique avait
            rabaisse cette carte au cran des elements d'action. */}
        <div className="bg-white rounded-carte p-7 sm:p-8"
             style={{ border: `1px solid ${BORDURE}`,
                      boxShadow: '0 1px 2px rgba(10,17,40,.04), 0 12px 32px rgba(10,17,40,.06)' }}>
          {children}
        </div>

        {pied && <div className="mt-6 text-center text-[13px] text-[var(--text-muted)]">{pied}</div>}

        <p className="mt-8 text-center text-[11px] text-[var(--text-muted)]">
          ASSO-PRO · Premier incubateur des associations en Afrique
        </p>
      </div>
    </div>
  );
}

/** Bouton principal, taille de cible confortable au doigt (48 px). */
export function BoutonPrincipal({ children, disabled, ...reste }) {
  return (
    <button
      {...reste}
      disabled={disabled}
      className="act act-tertiaire w-full"
      style={{ background: ACCENT, color: ENCRE }}
    >
      {children}
    </button>
  );
}

/** Champ de saisie avec icône, espacée du texte — pas collée au placeholder.
 *
 *  Les couleurs passent par `style` et non par des classes Tailwind : une
 *  classe construite à l'exécution (`border-[${BORDURE}]`) n'est jamais
 *  générée, car Tailwind lit le code source statiquement et ne voit que la
 *  chaîne littérale. Le champ serait resté sans bordure.
 */
export function Champ({ icone: Icone, ...reste }) {
  const [focus, setFocus] = React.useState(false);
  return (
    <div className="relative">
      {Icone && (
        <Icone className="absolute left-4 top-1/2 -translate-y-1/2 w-[17px] h-[17px]
                          text-[var(--text-muted)] pointer-events-none z-10" aria-hidden="true" />
      )}
      <input
        {...reste}
        onFocus={(e) => { setFocus(true); reste.onFocus?.(e); }}
        onBlur={(e) => { setFocus(false); reste.onBlur?.(e); }}
        className={`w-full h-12 rounded-pastille bg-white text-[14px] outline-none
                    transition-colors placeholder:text-[var(--text-muted)]
                    ${Icone ? 'pl-12' : 'pl-4'} pr-4`}
        style={{
          color: ENCRE,
          // 1,5 px pour que la prise de focus ne décale pas le contenu d'un
          // demi-pixel, ce qui fait « sauter » la ligne à chaque tabulation.
          border: `1.5px solid ${focus ? ACCENT : BORDURE}`,
          boxShadow: focus ? `0 0 0 3px ${ACCENT}22` : 'none',
        }}
      />
    </div>
  );
}

export function Message({ ton = 'erreur', children }) {
  if (!children) return null;
  const tons = {
    erreur: { fond: '#FEF2F2', bord: '#FECACA', texte: '#B42318' },
    info: { fond: '#FFFBEB', bord: '#FDE68A', texte: '#92400E' },
    succes: { fond: '#F0FDF4', bord: '#BBF7D0', texte: '#15803D' },
  };
  const t = tons[ton] || tons.erreur;
  return (
    <div role={ton === 'erreur' ? 'alert' : 'status'}
         className="rounded-pastille px-4 py-3 text-[13px] leading-relaxed"
         style={{ background: t.fond, border: `1px solid ${t.bord}`, color: t.texte }}>
      {children}
    </div>
  );
}
